import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

import Loading from "../../components/common/loading";
import { AppCard, AppPage, AppPageHeader } from "../../components/common/appShell";
import NotificationManager from "../../utils/notificationManager";
import {
  buildNotificationEventOptions,
  buildNotificationPresetSelections,
  DEVICE_CONNECTION_EVENT_CODE,
  formatHeadersInput,
  formatRecipientInput,
  parseHeadersInput,
  parseRecipientInput,
} from "../../utils/notificationRules";

import "./notifications.css";

function authConfig() {
  return {
    headers: {
      Authorization: "Bearer " + localStorage.getItem("token"),
    },
  };
}

function createDestination(kind) {
  const tempId = `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  if (kind === "email") {
    return {
      tempId,
      id: null,
      channel: "email",
      enabled: true,
      name: "Team Email",
      recipientsText: "",
    };
  }

  return {
    tempId,
    id: null,
    channel: "webhook",
    enabled: true,
    name: "Webhook",
    url: "",
    secret: "",
    hasSecret: false,
    headersText: "",
  };
}

function mapRuleToForm(rule = {}) {
  const eventSelections = (rule.eventSelections || []).reduce((sum, selection) => {
    const key = Number(selection.eventType);
    sum[key] = {
      eventType: key,
      notifyOnTriggered: !!selection.notifyOnTriggered,
      notifyOnCleared: !!selection.notifyOnCleared,
    };
    return sum;
  }, {});

  return {
    name: rule.name || "",
    enabled: rule.enabled !== false,
    senderDisplayName: rule.senderDisplayName || "",
    selectedDevices: (rule.targets || [])
      .filter((target) => target.targetType === "device")
      .map((target) => String(target.targetId)),
    selectedGroups: (rule.targets || [])
      .filter((target) => target.targetType === "group")
      .map((target) => String(target.targetId)),
    eventSelections,
    destinations: (rule.destinations || []).map((destination) => {
      if (destination.channel === "email") {
        return {
          tempId: destination.id || `existing-${destination.channel}-${Math.random()}`,
          id: destination.id || null,
          channel: destination.channel,
          enabled: destination.enabled !== false,
          name: destination.name || "Email",
          recipientsText: formatRecipientInput(destination?.config?.recipients || []),
        };
      }
      return {
        tempId: destination.id || `existing-${destination.channel}-${Math.random()}`,
        id: destination.id || null,
        channel: destination.channel,
        enabled: destination.enabled !== false,
        name: destination.name || "Webhook",
        url: destination?.config?.url || "",
        secret: "",
        hasSecret: !!destination?.config?.hasSecret,
        headersText: formatHeadersInput(destination?.config?.headers || {}),
      };
    }),
  };
}

function initialFormState() {
  return {
    name: "",
    enabled: true,
    senderDisplayName: "",
    selectedDevices: [],
    selectedGroups: [],
    eventSelections: {},
    destinations: [createDestination("email")],
  };
}

const TARGET_PAGE_SIZE = 24;

function createTargetDirectory() {
  return {
    devices: {},
    groups: {},
  };
}

function createTargetMeta() {
  return {
    currentPage: 1,
    limit: TARGET_PAGE_SIZE,
    totalCount: 0,
    totalPages: 0,
    search: "",
  };
}

function mapTargetEntries(type, entries = []) {
  return (entries || []).reduce((sum, entry) => {
    if (type === "devices") {
      sum.devices[String(entry.serial)] = {
        serial: Number(entry.serial),
        name: entry.name || `PDU ${entry.serial}`,
      };
      return sum;
    }

    sum.groups[String(entry.id)] = {
      id: Number(entry.id),
      name: entry.name || `Group ${entry.id}`,
      description: entry.description || "",
      itemCount: Number(entry.itemCount) || 0,
    };
    return sum;
  }, createTargetDirectory());
}

function buildTargetDirectoryFromRule(rule = {}) {
  return (rule.targets || []).reduce((sum, target) => {
    if (target.targetType === "device") {
      sum.devices[String(target.targetId)] = {
        serial: Number(target.targetId),
        name: target.targetName || `PDU ${target.targetId}`,
      };
      return sum;
    }

    if (target.targetType === "group") {
      sum.groups[String(target.targetId)] = {
        id: Number(target.targetId),
        name: target.targetName || `Group ${target.targetId}`,
        description: target.targetDescription || "",
        itemCount: Number(target.itemCount) || 0,
      };
    }

    return sum;
  }, createTargetDirectory());
}

function mergeTargetDirectories(current = createTargetDirectory(), next = createTargetDirectory()) {
  return {
    devices: {
      ...(current.devices || {}),
      ...(next.devices || {}),
    },
    groups: {
      ...(current.groups || {}),
      ...(next.groups || {}),
    },
  };
}

const EVENT_CATEGORY_CONFIG = [
  {
    key: "device",
    title: "Device & Connectivity",
    description: "Availability, AutoPing, route, and network state events.",
    matches: ({ code }) =>
      [DEVICE_CONNECTION_EVENT_CODE, 11, 12, 66, 67].includes(Number(code)),
  },
  {
    key: "power",
    title: "Power Transfer & System",
    description: "ATS, secondary source, and system power recovery events.",
    matches: ({ code }) => Number(code) >= 55 && Number(code) <= 59,
  },
  {
    key: "inlets",
    title: "Inlets",
    description: "Current, voltage, frequency, power factor, and energy thresholds.",
    matches: ({ code }) => Number(code) >= 13 && Number(code) <= 26,
  },
  {
    key: "banks",
    title: "Banks",
    description: "Breaker, current, voltage, frequency, and power thresholds.",
    matches: ({ code }) => Number(code) >= 30 && Number(code) <= 42,
  },
  {
    key: "outlets",
    title: "Outlets & Groups",
    description: "Outlet and group current threshold events.",
    matches: ({ code }) => Number(code) >= 45 && Number(code) <= 48,
  },
  {
    key: "sensors",
    title: "Sensors & I/O",
    description: "Temperature, humidity, digital input, switch, and sensor state events.",
    matches: ({ code }) =>
      (Number(code) >= 49 && Number(code) <= 52) ||
      (Number(code) >= 62 && Number(code) <= 65),
  },
  {
    key: "other",
    title: "Other Events",
    description: "Supported event types that do not fit another category.",
    matches: () => true,
  },
];

function buildEventGroups(eventOptions = []) {
  const groupsByKey = EVENT_CATEGORY_CONFIG.reduce((sum, category) => {
    sum[category.key] = {
      ...category,
      options: [],
    };
    return sum;
  }, {});

  eventOptions.forEach((option) => {
    const category = EVENT_CATEGORY_CONFIG.find((item) => item.matches(option));
    groupsByKey[category.key].options.push(option);
  });

  return EVENT_CATEGORY_CONFIG.map((category) => ({
    ...groupsByKey[category.key],
    options: groupsByKey[category.key].options.sort((a, b) => {
      if (category.key === "device") {
        if (Number(a.code) === DEVICE_CONNECTION_EVENT_CODE) {
          return -1;
        }
        if (Number(b.code) === DEVICE_CONNECTION_EVENT_CODE) {
          return 1;
        }
      }
      return Number(a.code) - Number(b.code);
    }),
  })).filter((category) => category.options.length > 0);
}

export default function NotificationRuleEditor(props) {
  const ruleId = props.match?.params?.notificationRuleId || null;
  const isEditing = Boolean(ruleId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [targetDirectory, setTargetDirectory] = useState(createTargetDirectory);
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [devicePage, setDevicePage] = useState(1);
  const [groupPage, setGroupPage] = useState(1);
  const [deviceMeta, setDeviceMeta] = useState(createTargetMeta);
  const [groupMeta, setGroupMeta] = useState(createTargetMeta);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [groupLoading, setGroupLoading] = useState(false);
  const [deviceLoadError, setDeviceLoadError] = useState("");
  const [groupLoadError, setGroupLoadError] = useState("");
  const [formError, setFormError] = useState("");
  const [openEventCategoryKeys, setOpenEventCategoryKeys] = useState([
    "device",
  ]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const ruleResponse = isEditing
          ? await axios.get(`/v1/notification-rules/${ruleId}`, authConfig())
          : null;
        if (cancelled) {
          return;
        }

        if (ruleResponse?.data?.data) {
          const rule = ruleResponse.data.data;
          setForm(mapRuleToForm(rule));
          setTargetDirectory((prev) =>
            mergeTargetDirectories(prev, buildTargetDirectoryFromRule(rule)),
          );
        } else {
          setForm(initialFormState());
        }
      } catch (error) {
        if (!cancelled) {
          NotificationManager.alert("Unable to load the notification rule editor.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [isEditing, ruleId]);

  useEffect(() => {
    let cancelled = false;

    async function loadTargetOptions() {
      setDeviceLoading(true);
      setDeviceLoadError("");
      try {
        const response = await axios.get("/v1/notification-rules/targets/devices", {
          ...authConfig(),
          params: {
            page: devicePage,
            limit: TARGET_PAGE_SIZE,
            ...(deviceSearch ? { search: deviceSearch } : {}),
          },
        });
        if (cancelled) {
          return;
        }

        const nextOptions = Array.isArray(response?.data?.data) ? response.data.data : [];
        setDeviceOptions(nextOptions);
        setDeviceMeta(response?.data?.meta || createTargetMeta());
        setTargetDirectory((prev) =>
          mergeTargetDirectories(prev, mapTargetEntries("devices", nextOptions)),
        );
      } catch (error) {
        if (!cancelled) {
          setDeviceOptions([]);
          setDeviceMeta(createTargetMeta());
          setDeviceLoadError("Unable to load available devices.");
        }
      } finally {
        if (!cancelled) {
          setDeviceLoading(false);
        }
      }
    }

    loadTargetOptions();
    return () => {
      cancelled = true;
    };
  }, [devicePage, deviceSearch]);

  useEffect(() => {
    let cancelled = false;

    async function loadTargetOptions() {
      setGroupLoading(true);
      setGroupLoadError("");
      try {
        const response = await axios.get("/v1/notification-rules/targets/groups", {
          ...authConfig(),
          params: {
            page: groupPage,
            limit: TARGET_PAGE_SIZE,
            ...(groupSearch ? { search: groupSearch } : {}),
          },
        });
        if (cancelled) {
          return;
        }

        const nextOptions = Array.isArray(response?.data?.data) ? response.data.data : [];
        setGroupOptions(nextOptions);
        setGroupMeta(response?.data?.meta || createTargetMeta());
        setTargetDirectory((prev) =>
          mergeTargetDirectories(prev, mapTargetEntries("groups", nextOptions)),
        );
      } catch (error) {
        if (!cancelled) {
          setGroupOptions([]);
          setGroupMeta(createTargetMeta());
          setGroupLoadError("Unable to load available groups.");
        }
      } finally {
        if (!cancelled) {
          setGroupLoading(false);
        }
      }
    }

    loadTargetOptions();
    return () => {
      cancelled = true;
    };
  }, [groupPage, groupSearch]);

  const eventOptions = useMemo(() => buildNotificationEventOptions(), []);
  const eventGroups = useMemo(() => buildEventGroups(eventOptions), [eventOptions]);

  const selectedEventKeys = useMemo(
    () =>
      Object.keys(form.eventSelections)
        .map((key) => Number(key))
        .filter((key) => Number.isFinite(key)),
    [form.eventSelections],
  );
  const selectedEventKeySet = useMemo(
    () => new Set(selectedEventKeys.map((key) => Number(key))),
    [selectedEventKeys],
  );
  const allEventsSelected = useMemo(
    () =>
      eventOptions.length > 0 &&
      eventOptions.every((option) => selectedEventKeySet.has(Number(option.code))),
    [eventOptions, selectedEventKeySet],
  );

  const selectedDeviceEntries = useMemo(
    () =>
      form.selectedDevices.map((id) => ({
        serial: Number(id),
        name: targetDirectory.devices?.[String(id)]?.name || `PDU ${id}`,
      })),
    [form.selectedDevices, targetDirectory.devices],
  );

  const selectedGroupEntries = useMemo(
    () =>
      form.selectedGroups.map((id) => {
        const group = targetDirectory.groups?.[String(id)] || {};
        return {
          id: Number(id),
          name: group.name || `Group ${id}`,
          itemCount: Number(group.itemCount) || 0,
        };
      }),
    [form.selectedGroups, targetDirectory.groups],
  );

  const toggleTarget = (type, id) => {
    setForm((prev) => {
      const key = type === "device" ? "selectedDevices" : "selectedGroups";
      const current = new Set(prev[key]);
      const stringId = String(id);
      if (current.has(stringId)) {
        current.delete(stringId);
      } else {
        current.add(stringId);
      }
      return {
        ...prev,
        [key]: Array.from(current),
      };
    });
  };

  const toggleAllSupportedEvents = () => {
    if (allEventsSelected) {
      setForm((prev) => ({
        ...prev,
        eventSelections: {},
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      eventSelections: buildNotificationPresetSelections(
        "allSupportedEvents",
        eventOptions,
      ),
    }));
    if (eventGroups.length > 0) {
      setOpenEventCategoryKeys(eventGroups.map((group) => group.key));
    }
  };

  const toggleEventCategory = (categoryKey) => {
    setOpenEventCategoryKeys((current) =>
      current.includes(categoryKey)
        ? current.filter((key) => key !== categoryKey)
        : [...current, categoryKey],
    );
  };

  const toggleEventSelection = (eventType) => {
    const numericType = Number(eventType);
    setForm((prev) => {
      const next = { ...prev.eventSelections };
      if (next[numericType]) {
        delete next[numericType];
      } else {
        next[numericType] = {
          eventType: numericType,
          notifyOnTriggered: true,
          notifyOnCleared: numericType === DEVICE_CONNECTION_EVENT_CODE,
        };
      }
      return {
        ...prev,
        eventSelections: next,
      };
    });
  };

  const updateEventFlag = (eventType, flag) => {
    const numericType = Number(eventType);
    setForm((prev) => {
      const current = prev.eventSelections[numericType];
      if (!current) {
        return prev;
      }
      const nextSelections = {
        ...prev.eventSelections,
      };
      const nextSelection = {
        ...current,
        [flag]: !current[flag],
      };
      if (!nextSelection.notifyOnTriggered && !nextSelection.notifyOnCleared) {
        delete nextSelections[numericType];
        return {
          ...prev,
          eventSelections: nextSelections,
        };
      }
      return {
        ...prev,
        eventSelections: {
          ...nextSelections,
          [numericType]: nextSelection,
        },
      };
    });
  };

  const updateDestination = (tempId, updater) => {
    setForm((prev) => ({
      ...prev,
      destinations: prev.destinations.map((destination) =>
        destination.tempId === tempId
          ? { ...destination, ...updater }
          : destination,
      ),
    }));
  };

  const addDestination = (channel) => {
    setForm((prev) => ({
      ...prev,
      destinations: [...prev.destinations, createDestination(channel)],
    }));
  };

  const removeDestination = (tempId) => {
    setForm((prev) => ({
      ...prev,
      destinations: prev.destinations.filter(
        (destination) => destination.tempId !== tempId,
      ),
    }));
  };

  const buildPayload = () => {
    const destinations = [];
    for (const destination of form.destinations) {
      if (destination.channel === "email") {
        destinations.push({
          id: destination.id || undefined,
          channel: "email",
          name: destination.name,
          enabled: destination.enabled,
          config: {
            recipients: parseRecipientInput(destination.recipientsText),
          },
        });
        continue;
      }

      const parsedHeaders = parseHeadersInput(destination.headersText);
      if (parsedHeaders.error) {
        return {
          error: parsedHeaders.error,
          payload: null,
        };
      }

      destinations.push({
        id: destination.id || undefined,
        channel: "webhook",
        name: destination.name,
        enabled: destination.enabled,
        config: {
          url: destination.url,
          headers: parsedHeaders.value,
          ...(destination.secret
            ? { secret: destination.secret }
            : destination.hasSecret
            ? { preserveSecret: true }
            : {}),
        },
      });
    }

    return {
      error: "",
      payload: {
        name: form.name,
        enabled: form.enabled,
        senderDisplayName: form.senderDisplayName,
        targets: [
          ...form.selectedDevices.map((id) => ({ type: "device", id })),
          ...form.selectedGroups.map((id) => ({ type: "group", id })),
        ],
        eventSelections: selectedEventKeys.map((key) => form.eventSelections[key]),
        destinations,
      },
    };
  };

  const saveRule = async () => {
    const { error, payload } = buildPayload();
    if (error) {
      setFormError(error);
      NotificationManager.alert(error);
      return;
    }

    setSaving(true);
    setFormError("");
    try {
      if (isEditing) {
        await axios.put(`/v1/notification-rules/${ruleId}`, payload, authConfig());
        NotificationManager.success("Notification rule updated successfully.");
      } else {
        await axios.post("/v1/notification-rules", payload, authConfig());
        NotificationManager.success("Notification rule created successfully.");
      }
      props.history.push("/team/notifications");
    } catch (error) {
      const errors = error?.response?.data?.errors;
      const message = Array.isArray(errors) && errors.length
        ? errors.join(" ")
        : "Unable to save the notification rule.";
      setFormError(message);
      NotificationManager.alert(message);
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async () => {
    if (!isEditing) {
      return;
    }
    const confirmed = window.confirm(
      "Delete this notification rule and stop future deliveries?",
    );
    if (!confirmed) {
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`/v1/notification-rules/${ruleId}`, authConfig());
      NotificationManager.success("Notification rule deleted.");
      props.history.push("/team/notifications");
    } catch (error) {
      NotificationManager.alert("Unable to delete the notification rule.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <Loading black />;
  }

  return (
    <AppPage className="notifications-page">
      <AppPageHeader
        eyebrow="Team"
        title={isEditing ? "Edit Notification Rule" : "Create Notification Rule"}
        description="Configure targets, event states, and email or webhook destinations."
        actions={
          <>
            <button
              type="button"
              className="button secondary"
              onClick={() => props.history.push("/team/notifications")}
            >
              Back to Rules
            </button>
            {isEditing && (
              <button
                type="button"
                className="button alert"
                onClick={deleteRule}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete Rule"}
              </button>
            )}
            <button
              type="button"
              className="button"
              onClick={saveRule}
              disabled={saving}
            >
              {saving ? "Saving..." : isEditing ? "Save Changes" : "Create Rule"}
            </button>
          </>
        }
      />
      {formError ? <p className="notifications-error">{formError}</p> : null}

      <form
        className="notifications-form"
        onSubmit={(event) => {
          event.preventDefault();
          saveRule();
        }}
      >
        <AppCard
          title="Rule Details"
          description="Set the internal name and sender display name."
        >
          <div className="notifications-field-grid">
            <div className="notifications-field">
              <label htmlFor="notification-rule-name">Rule name</label>
              <input
                id="notification-rule-name"
                value={form.name}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, name: event.target.value }))
                }
                placeholder="Example: Critical Rack Offline"
              />
            </div>
            <div className="notifications-field">
              <label htmlFor="notification-rule-sender">Sender display name</label>
              <input
                id="notification-rule-sender"
                value={form.senderDisplayName}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    senderDisplayName: event.target.value,
                  }))
                }
                placeholder="Example: Synaccess NOC"
              />
            </div>
          </div>
          <div className="notifications-inline-flags" style={{ marginTop: 16 }}>
            <label>
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, enabled: event.target.checked }))
                }
              />
              Rule enabled
            </label>
          </div>
        </AppCard>

        <AppCard title="Targets" description="Select devices and groups for this rule.">
          <div className="notifications-target-grid">
            <TargetPicker
              title="Devices"
              searchId="notification-device-search"
              searchLabel="Search by device name or serial"
              searchPlaceholder="PDU name or serial"
              searchValue={deviceSearch}
              onSearchChange={(value) => {
                setDeviceSearch(value);
                setDevicePage(1);
              }}
              selectedEntries={selectedDeviceEntries}
              options={deviceOptions}
              loading={deviceLoading}
              loadError={deviceLoadError}
              emptyText="No matching devices found."
              meta={deviceMeta}
              resultLabel="devices"
              onToggle={(id) => toggleTarget("device", id)}
              onPageChange={setDevicePage}
              getEntryId={(device) => device.serial}
              getEntryTitle={(device) => device.name}
              getEntryMeta={(device) => `Serial ${device.serial}`}
            />
            <TargetPicker
              title="Device Groups"
              searchId="notification-group-search"
              searchLabel="Search by group name"
              searchPlaceholder="Group name"
              searchValue={groupSearch}
              onSearchChange={(value) => {
                setGroupSearch(value);
                setGroupPage(1);
              }}
              selectedEntries={selectedGroupEntries}
              options={groupOptions}
              loading={groupLoading}
              loadError={groupLoadError}
              emptyText="No matching groups found."
              meta={groupMeta}
              resultLabel="groups"
              onToggle={(id) => toggleTarget("group", id)}
              onPageChange={setGroupPage}
              getEntryId={(group) => group.id}
              getEntryTitle={(group) => group.name}
              getEntryMeta={(group) => `${group.itemCount || 0} devices`}
            />
          </div>
        </AppCard>

        <AppCard
          title="Events"
          description={
            <>
              Choose the event types and states that send notifications.{" "}
              <Link
                className="notifications-inline-link"
                to="/alerts-documentation"
              >
                View event documentation
              </Link>
              .
            </>
          }
        >
          <div className="notifications-preset-row">
            <button
              type="button"
              className="button secondary"
              onClick={toggleAllSupportedEvents}
            >
              {allEventsSelected ? "Clear All Events" : "Select All Events"}
            </button>
          </div>
          <EventCategoryPicker
            eventGroups={eventGroups}
            eventSelections={form.eventSelections}
            openCategoryKeys={openEventCategoryKeys}
            selectedEventKeySet={selectedEventKeySet}
            onToggleCategory={toggleEventCategory}
            onToggleEvent={toggleEventSelection}
            onUpdateEventFlag={updateEventFlag}
          />
        </AppCard>

        <AppCard
          title="Destinations"
          description="Send to email lists or webhook endpoints."
          actions={
            <>
              <button
                type="button"
                className="button secondary"
                onClick={() => addDestination("email")}
              >
                Add Email
              </button>
              <button
                type="button"
                className="button secondary"
                onClick={() => addDestination("webhook")}
              >
                Add Webhook
              </button>
            </>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {form.destinations.map((destination) => (
              <div className="notifications-destination-card" key={destination.tempId}>
                <div className="notifications-destination-header">
                  <div className="notifications-destination-heading">
                    <span className="material-icons notifications-destination-icon">
                      {destination.channel === "email" ? "mail" : "webhook"}
                    </span>
                    <div>
                      <h3 className="notifications-destination-title">
                        {destination.channel === "email"
                          ? "Email Destination"
                          : "Webhook Destination"}
                      </h3>
                      <p className="notifications-destination-copy">
                        {destination.channel === "email"
                          ? "HTML email notification"
                          : "JSON webhook delivery"}
                      </p>
                    </div>
                  </div>
                  <div className="notifications-row-actions">
                    <label
                      className={`notifications-switch ${
                        destination.enabled ? "is-on" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={destination.enabled}
                        onChange={(event) =>
                          updateDestination(destination.tempId, {
                            enabled: event.target.checked,
                          })
                        }
                      />
                      <span className="notifications-switch-track">
                        <span className="notifications-switch-knob" />
                      </span>
                      <span className="notifications-switch-label">
                        {destination.enabled ? "Enabled" : "Disabled"}
                      </span>
                    </label>
                    <button
                      type="button"
                      style={{
                        marginBottom: "0px"
                      }}
                      className="button alert notifications-compact-button"
                      onClick={() => removeDestination(destination.tempId)}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                <div className="notifications-destination-body">
                  <div className="notifications-field-grid">
                    <div className="notifications-field">
                      <label>Destination name</label>
                      <input
                        value={destination.name}
                        onChange={(event) =>
                          updateDestination(destination.tempId, {
                            name: event.target.value,
                          })
                        }
                      />
                    </div>
                    {destination.channel === "webhook" ? (
                      <div className="notifications-field">
                        <label>Webhook URL</label>
                        <input
                          value={destination.url}
                          onChange={(event) =>
                            updateDestination(destination.tempId, {
                              url: event.target.value,
                            })
                          }
                          placeholder="https://example.com/notifications"
                        />
                      </div>
                    ) : null}
                  </div>

                  {destination.channel === "email" ? (
                    <div className="notifications-field">
                      <label>Recipients</label>
                      <textarea
                        value={destination.recipientsText}
                        onChange={(event) =>
                          updateDestination(destination.tempId, {
                            recipientsText: event.target.value,
                          })
                        }
                        placeholder={"ops@example.com\nalerts@example.com"}
                      />
                      <div className="notifications-help">
                        Separate recipients with commas, semicolons, or new lines.
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="notifications-field-grid">
                        <div className="notifications-field">
                          <label>Signing secret</label>
                          <input
                            type="password"
                            value={destination.secret}
                            onChange={(event) =>
                              updateDestination(destination.tempId, {
                                secret: event.target.value,
                              })
                            }
                            placeholder={
                              destination.hasSecret
                                ? "Leave blank to keep the current secret"
                                : "Optional shared secret"
                            }
                          />
                          {destination.hasSecret && !destination.secret ? (
                            <div className="notifications-help">
                              A secret is already configured for this destination.
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="notifications-field">
                        <label>Custom headers (JSON)</label>
                        <textarea
                          value={destination.headersText}
                          onChange={(event) =>
                            updateDestination(destination.tempId, {
                              headersText: event.target.value,
                            })
                          }
                          placeholder={'{\n  "x-team": "ops"\n}'}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </AppCard>
      </form>
    </AppPage>
  );
}

function TargetPicker({
  title,
  searchId,
  searchLabel,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  selectedEntries,
  options,
  loading,
  loadError,
  emptyText,
  meta,
  resultLabel,
  onToggle,
  onPageChange,
  getEntryId,
  getEntryTitle,
  getEntryMeta,
}) {
  const selectedIdSet = new Set(
    (selectedEntries || []).map((entry) => String(getEntryId(entry))),
  );
  const currentPage = Number(meta?.currentPage || 1);
  const totalPages = Number(meta?.totalPages || 0);

  return (
    <section className="notifications-picker">
      <div className="notifications-picker-header">
        <h3 className="notifications-subsection-title">{title}</h3>
        <span className="notifications-picker-count">
          {selectedEntries.length} selected
        </span>
      </div>

      <div className="notifications-field">
        <label htmlFor={searchId}>{searchLabel}</label>
        <input
          id={searchId}
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
        />
      </div>

      {selectedEntries.length > 0 ? (
        <div className="notifications-selected-list">
          {selectedEntries.map((entry) => {
            const id = getEntryId(entry);
            return (
              <button
                key={id}
                type="button"
                className="notifications-selected-pill"
                onClick={() => onToggle(id)}
                aria-label={`Remove ${getEntryTitle(entry)}`}
              >
                <span>{getEntryTitle(entry)}</span>
                <i className="material-icons">close</i>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="notifications-picker-empty-selection">
          No {resultLabel} selected.
        </div>
      )}

      {loadError ? <p className="notifications-error">{loadError}</p> : null}

      <div className="notifications-picker-list">
        {loading ? (
          <div className="notifications-picker-state">Loading {resultLabel}...</div>
        ) : options.length === 0 ? (
          <div className="notifications-empty">{emptyText}</div>
        ) : (
          options.map((entry) => {
            const id = getEntryId(entry);
            const selected = selectedIdSet.has(String(id));
            return (
              <label
                className={`notifications-picker-row ${
                  selected ? "is-selected" : ""
                }`}
                key={id}
              >
                <input
                  className="notifications-native-check"
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggle(id)}
                />
                <span className="notifications-select-mark" aria-hidden="true" />
                <span className="notifications-picker-row-copy">
                  <span className="notifications-picker-row-title">
                    {getEntryTitle(entry)}
                  </span>
                  <span className="notifications-picker-row-meta">
                    {getEntryMeta(entry)}
                  </span>
                </span>
              </label>
            );
          })
        )}
      </div>

      <div className="notifications-target-pagination">
        <div className="notifications-muted">
          Showing {options.length} of {meta?.totalCount || 0} {resultLabel}
        </div>
        <div className="notifications-row-actions">
          <button
            type="button"
            className="button secondary notifications-compact-button"
            disabled={loading || currentPage <= 1}
            onClick={() => onPageChange((prev) => Math.max(prev - 1, 1))}
          >
            Previous
          </button>
          <span className="notifications-muted">
            Page {currentPage} of {Math.max(totalPages, 1)}
          </span>
          <button
            type="button"
            className="button secondary notifications-compact-button"
            disabled={loading || totalPages === 0 || currentPage >= totalPages}
            onClick={() =>
              onPageChange((prev) => Math.min(prev + 1, Math.max(totalPages, 1)))
            }
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
}

function EventCategoryPicker({
  eventGroups,
  eventSelections,
  openCategoryKeys,
  selectedEventKeySet,
  onToggleCategory,
  onToggleEvent,
  onUpdateEventFlag,
}) {
  return (
    <div className="notifications-event-picker">
      {eventGroups.map((group) => {
        const isOpen = openCategoryKeys.includes(group.key);
        const selectedCount = group.options.filter((option) =>
          selectedEventKeySet.has(Number(option.code)),
        ).length;

        return (
          <section className="notifications-event-category" key={group.key}>
            <button
              type="button"
              className="notifications-event-category-toggle"
              onClick={() => onToggleCategory(group.key)}
              aria-expanded={isOpen}
            >
              <span className="notifications-event-category-main">
                <i className="material-icons">
                  {isOpen ? "expand_more" : "chevron_right"}
                </i>
                <span>
                  <span className="notifications-event-category-title">
                    {group.title}
                  </span>
                  <span className="notifications-event-category-description">
                    {group.description}
                  </span>
                </span>
              </span>
              <span className="notifications-event-category-count">
                {selectedCount} / {group.options.length}
              </span>
            </button>

            {isOpen ? (
              <div className="notifications-event-list">
                {group.options.map((option) => {
                  const selection = eventSelections[option.code];
                  const isConnectionEvent =
                    option.code === DEVICE_CONNECTION_EVENT_CODE;

                  return (
                    <div
                      className={`notifications-event-row ${
                        selection ? "is-selected" : ""
                      }`}
                      key={option.code}
                    >
                      <label className="notifications-event-name">
                        <input
                          className="notifications-native-check"
                          type="checkbox"
                          checked={!!selection}
                          onChange={() => onToggleEvent(option.code)}
                        />
                        <span
                          className="notifications-select-mark"
                          aria-hidden="true"
                        />
                        <span>
                          <span className="notifications-event-title">
                            {option.name}
                          </span>
                          <span className="notifications-event-code">
                            Code {option.code}
                          </span>
                        </span>
                      </label>

                      {selection ? (
                        <div className="notifications-event-state-controls">
                          <EventStateToggle
                            checked={selection.notifyOnTriggered}
                            label={isConnectionEvent ? "Offline" : "Triggered"}
                            onChange={() =>
                              onUpdateEventFlag(
                                option.code,
                                "notifyOnTriggered",
                              )
                            }
                          />
                          <EventStateToggle
                            checked={selection.notifyOnCleared}
                            label={isConnectionEvent ? "Online" : "Cleared"}
                            onChange={() =>
                              onUpdateEventFlag(option.code, "notifyOnCleared")
                            }
                          />
                        </div>
                      ) : (
                        <span className="notifications-event-unselected">
                          Not selected
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function EventStateToggle({ checked, label, onChange }) {
  return (
    <label className={`notifications-event-state ${checked ? "is-active" : ""}`}>
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span>{label}</span>
    </label>
  );
}
